import { useRef, useEffect, useCallback } from "react";
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Undo2, Redo2, Baseline,
} from "lucide-react";

const FONT_SIZES = [
  { label: "Small", value: "1" },
  { label: "Normal", value: "3" },
  { label: "Large", value: "4" },
  { label: "X-Large", value: "5" },
  { label: "Huge", value: "7" },
];

const FONT_FAMILIES = [
  "Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Trebuchet MS",
];

function ToolBtn({ onClick, title, active, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`p-1.5 rounded text-sm transition-colors ${
        active ? "bg-[#032352] text-white" : "text-[#5B5C5F] hover:bg-slate-100 hover:text-[#032352]"
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);

  // Load initial value once
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      editorRef.current.innerHTML = value;
    }
    // eslint-disable-next-line
  }, []);

  const emit = useCallback(() => {
    onChange?.(editorRef.current?.innerHTML || "");
  }, [onChange]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  }, [emit]);

  const applyColor = useCallback((e) => {
    exec("foreColor", e.target.value);
  }, [exec]);

  const applyBg = useCallback((e) => {
    exec("hiliteColor", e.target.value);
  }, [exec]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-slate-50 border-b border-slate-200">
        {/* Undo / Redo */}
        <ToolBtn onClick={() => exec("undo")} title="Undo"><Undo2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("redo")} title="Redo"><Redo2 className="w-3.5 h-3.5" /></ToolBtn>
        <Divider />

        {/* Font family */}
        <select
          className="text-xs border border-slate-200 rounded px-1 py-1 text-[#5B5C5F] bg-white focus:outline-none"
          defaultValue=""
          onChange={(e) => { exec("fontName", e.target.value); e.target.value = ""; }}
          title="Font family"
        >
          <option value="" disabled>Font</option>
          {FONT_FAMILIES.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
          ))}
        </select>

        {/* Font size */}
        <select
          className="text-xs border border-slate-200 rounded px-1 py-1 text-[#5B5C5F] bg-white focus:outline-none"
          defaultValue=""
          onChange={(e) => { exec("fontSize", e.target.value); e.target.value = ""; }}
          title="Font size"
        >
          <option value="" disabled>Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <Divider />

        {/* Format */}
        <ToolBtn onClick={() => exec("bold")} title="Bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Underline"><Underline className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("strikeThrough")} title="Strikethrough"><Strikethrough className="w-3.5 h-3.5" /></ToolBtn>
        <Divider />

        {/* Colour */}
        <label title="Text colour" className="relative cursor-pointer p-1.5 rounded hover:bg-slate-100">
          <Baseline className="w-3.5 h-3.5 text-[#5B5C5F]" />
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            defaultValue="#000000"
            onChange={applyColor}
            title="Text colour"
          />
        </label>
        <label title="Highlight colour" className="relative cursor-pointer p-1.5 rounded hover:bg-slate-100">
          <span className="text-[10px] font-bold text-[#5B5C5F] leading-none select-none">BG</span>
          <input
            type="color"
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            defaultValue="#ffff00"
            onChange={applyBg}
            title="Highlight colour"
          />
        </label>
        <Divider />

        {/* Alignment */}
        <ToolBtn onClick={() => exec("justifyLeft")} title="Align left"><AlignLeft className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("justifyCenter")} title="Align centre"><AlignCenter className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("justifyRight")} title="Align right"><AlignRight className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("justifyFull")} title="Justify"><AlignJustify className="w-3.5 h-3.5" /></ToolBtn>
        <Divider />

        {/* Lists */}
        <ToolBtn onClick={() => exec("insertUnorderedList")} title="Bullet list"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder || "Start typing..."}
        onInput={emit}
        onKeyUp={emit}
        className="p-4 min-h-48 focus:outline-none text-sm text-[#032352] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
        style={{ lineHeight: 1.6 }}
      />
    </div>
  );
}
